import {
  Query,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  APIQueryDataUpdater,
  APIQueryOptions,
  APIQueryResult,
  setAPIQueryData,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { useAPISocketMessages } from 'shared/api/APISocketMessages';
import {
  requestJSONLoadboardAPI,
  requestLoadboardAPI,
} from 'shared/api/LoadboardAPIClient';
import { useFlag } from 'shared/settings/FeatureToggles';
import {
  LoadSuggestionDTO,
  loadSuggestionListSchema,
} from './LoadSuggestionDTO';

export function setLoadSuggestionsQueryData(
  queryClient: QueryClient,
  updater: APIQueryDataUpdater<LoadSuggestionDTO[]>,
): void {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['suggested-loads', 'items'], exact: false }) as Array<
    Query<LoadSuggestionDTO[]>
  >;

  for (let query of queries) {
    if (query.state.data) {
      query.setData(updater(query.state.data));
    }
  }
}

export function useLoadSuggestions(
  drivers: string[],
  options?: APIQueryOptions<LoadSuggestionDTO[]>,
): APIQueryResult<LoadSuggestionDTO[]> {
  const isZodSchemaEnabled = useFlag('ctms_loadboard_zod_schema');

  return useAPIQuery(
    ['suggested-loads', 'items', { drivers }],
    () => {
      return requestJSONLoadboardAPI([
        'GET /internal/v3/loads/suggestions{?driver_guids}',
        { driver_guids: drivers },
      ]);
    },
    {
      ...options,
      schema: isZodSchemaEnabled ? loadSuggestionListSchema : undefined,
    },
  );
}

interface LoadSuggestionsUnseenCount {
  number_of_unseen_suggestions: number;
}

export function useLoadSuggestionsUnseenCount(): APIQueryResult<LoadSuggestionsUnseenCount> {
  return useAPIQuery(
    ['suggested-loads', 'unseen'],
    () => requestJSONLoadboardAPI('GET /internal/v3/loads/suggestions/unseen'),
    { staleTime: Infinity },
  );
}

export function useLoadSuggestionsUnseenCountPerDriver(
  driverGuid: string,
  options?: APIQueryOptions<LoadSuggestionsUnseenCount>,
): APIQueryResult<LoadSuggestionsUnseenCount> {
  return useAPIQuery(
    ['suggested-loads', 'unseen', { driverGuid }],
    () =>
      requestJSONLoadboardAPI([
        'GET /internal/v3/loads/suggestions/unseen{?driver_guid}',
        { driver_guid: driverGuid },
      ]),
    options,
  );
}

export function useLoadSuggestionsCache() {
  const client = useQueryClient();

  return useMemo(() => {
    function setLoadSuggestionsUnseenCount(
      updater: APIQueryDataUpdater<LoadSuggestionsUnseenCount>,
    ): void {
      setAPIQueryData(client, ['suggested-loads', 'unseen'], updater);
    }

    function invalidateLoadSuggestions() {
      void client.invalidateQueries({ queryKey: ['suggested-loads'] });
    }

    function invalidateLoadSuggestionsList() {
      void client.invalidateQueries({ queryKey: ['suggested-loads', 'items'] });
    }

    return {
      invalidateLoadSuggestions,
      setLoadSuggestionsUnseenCount,
      invalidateLoadSuggestionsList,
    };
  }, [client]);
}

export function useDismissLoadSuggestion(
  options?: UseMutationOptions<Response, Error, number>,
): UseMutationResult<Response, Error, number> {
  const queryClient = useQueryClient();
  const previousLoads = queryClient.getQueryData<
    APIQueryDataUpdater<LoadSuggestionDTO[]>
  >(['suggested-loads', 'items']);

  return useMutation({
    mutationFn: (suggestionId) => {
      return requestLoadboardAPI([
        'POST /internal/v3/loads/suggestions/{suggestionId}/dismiss',
        { suggestionId },
      ]);
    },
    ...options,
    onMutate: (suggestionId, context) => {
      setLoadSuggestionsQueryData(queryClient, (loads) =>
        loads.filter((load) => load.suggestion_id !== suggestionId),
      );
      void options?.onMutate?.(suggestionId, context);
    },
    onError: (...args) => {
      queryClient.setQueryData(['suggested-loads', 'items'], previousLoads);
      void options?.onError?.(...args);
    },
  });
}

export function useMarkAsSeenLoadSuggestion({
  invalidateCache = true,
  ...options
}: UseMutationOptions<Response, Error, Set<number>> & {
  invalidateCache?: boolean;
} = {}): UseMutationResult<Response, Error, Set<number>> {
  const queryClient = useQueryClient();
  const { invalidateLoadSuggestions } = useLoadSuggestionsCache();

  return useMutation({
    mutationFn: (ids) =>
      requestLoadboardAPI('POST /internal/v3/loads/suggestions/mark_as_seen', {
        json: { suggestion_ids: Array.from(ids) },
      }).then((response) => {
        if (!invalidateCache) {
          return response;
        }

        setLoadSuggestionsQueryData(queryClient, (loads) =>
          loads.map((load) => {
            if (ids.has(load.suggestion_id)) {
              return { ...load, is_seen: true };
            }
            return load;
          }),
        );
        invalidateLoadSuggestions();

        return response;
      }),
    ...options,
  });
}

interface LoadSuggestionsUpdatedData {
  subject: 'Load Suggestion Deactivated' | 'New Load Suggestion Added';
}

/*
 * This hook should be called once because it updates global cache.
 */
export function useLoadSuggestionCountSubscription() {
  const { data } = useLoadSuggestionsUnseenCount();

  const { invalidateLoadSuggestionsList, setLoadSuggestionsUnseenCount } =
    useLoadSuggestionsCache();

  useAPISocketMessages<LoadSuggestionsUpdatedData>((payload) => {
    if (payload.meta.type === 'load_suggestions_updated') {
      setLoadSuggestionsUnseenCount(
        ({ number_of_unseen_suggestions: count }) => {
          count +=
            payload.data.subject === 'New Load Suggestion Added' ? 1 : -1;
          return { number_of_unseen_suggestions: Math.max(count, 0) };
        },
      );

      invalidateLoadSuggestionsList();
    }
  });

  return data;
}
